'use client';

import type React from 'react';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTheme } from '../../contexts/ThemeContext';

const SwitcherWrapper = styled.div<{ $visible: boolean }>`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9970;
  display: flex;
  align-items: center;
  gap: 8px;
  opacity: ${({ $visible }) => ($visible ? 1 : 0)};
  transform: ${({ $visible }) => ($visible ? 'translateY(0)' : 'translateY(-15px)')};
  pointer-events: ${({ $visible }) => ($visible ? 'all' : 'none')};
  transition:
    opacity 0.3s ease,
    transform 0.3s ease;

  @media (max-width: 480px) {
    top: 14px;
    right: 14px;
    gap: 6px;
  }
`;

const LangPill = styled.div`
  display: flex;
  align-items: center;
  background: var(--nav-bg, rgba(14, 14, 24, 0.85));
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--nav-border, rgba(255, 0, 255, 0.25));
  border-radius: 20px;
  padding: 3px 6px;
  box-shadow: 0 4px 20px var(--card-shadow, rgba(0, 0, 0, 0.4));
  transition:
    border-color 0.2s ease,
    background-color 0.3s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: rgba(0, 255, 0, 0.5);
    box-shadow: 0 0 15px rgba(0, 255, 0, 0.3);
  }

  @media (max-width: 480px) {
    padding: 2px 4px;
  }
`;

const LangButton = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => ($active ? '#00ff00' : 'transparent')};
  color: ${({ $active }) => ($active ? '#000000' : 'var(--text-secondary, rgba(255, 255, 255, 0.75))')};
  border: none;
  border-radius: 14px;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 1px;
  font-variant-caps: all-petite-caps;
  padding: 4px 9px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    color: ${({ $active }) => ($active ? '#000000' : '#ff2d95')};
  }
`;

const Separator = styled.span`
  color: var(--text-muted, rgba(255, 255, 255, 0.25));
  font-size: 0.75rem;
  margin: 0 2px;
  user-select: none;
`;

const ThemeButton = styled.button`
  background: var(--nav-bg, rgba(14, 14, 24, 0.85));
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--nav-border, rgba(255, 0, 255, 0.25));
  border-radius: 50%;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ff2d95;
  cursor: pointer;
  box-shadow: 0 4px 20px var(--card-shadow, rgba(0, 0, 0, 0.4));
  transition: all 0.2s ease;

  &:hover {
    color: #00ff00;
    border-color: rgba(0, 255, 0, 0.5);
    transform: scale(1.08);
    box-shadow: 0 0 15px rgba(0, 255, 0, 0.3);
  }

  svg {
    width: 17px;
    height: 17px;
  }

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

export const LanguageSwitcher: React.FC = () => {
  const { language, setLanguage, t } = useLanguage();
  const { isDark, toggleTheme } = useTheme();
  const [isHeroVisible, setIsHeroVisible] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset;
      const threshold = window.innerHeight * 0.45;
      setIsHeroVisible(scrollY <= threshold);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <SwitcherWrapper $visible={isHeroVisible} role="group" aria-label={t('selectLanguage')}>
      <LangPill>
        <LangButton
          type="button"
          $active={language === 'en'}
          onClick={() => setLanguage('en')}
          aria-pressed={language === 'en'}
          aria-label="Switch to English"
        >
          EN
        </LangButton>
        <Separator>|</Separator>
        <LangButton
          type="button"
          $active={language === 'de'}
          onClick={() => setLanguage('de')}
          aria-pressed={language === 'de'}
          aria-label="Auf Deutsch wechseln"
        >
          DE
        </LangButton>
      </LangPill>

      <ThemeButton
        type="button"
        onClick={toggleTheme}
        aria-label={isDark ? t('toggleThemeLight') : t('toggleThemeDark')}
        title={isDark ? t('toggleThemeLight') : t('toggleThemeDark')}
      >
        {isDark ? (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
        ) : (
          <svg
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        )}
      </ThemeButton>
    </SwitcherWrapper>
  );
};

export default LanguageSwitcher;
